/**
 * Advanced (BI) starts from the question (CU-869fmk5ge, piece 1, D4): the six goals, where each one leads, and what it
 * fills when it fills the form — with the reason of every value (CA 4-5).
 *
 * One registry, no flag and no dates (T8): a goal opens its dashboard **only when the menu offers it**
 * (`businessLogisticsChildren`, the same list that builds the menu and the routes). When a dashboard ships, its menu
 * entry switches the goal by itself; where it is not offered (e.g. `master` today) the goal fills the equivalent query.
 *
 * Imports only alias-free modules: `node --test` runs it.
 */
import { addDays, startOfDay, toCompact } from '../../../Dashboards/@services/dates.js'
import { resolveRange } from '../../../Dashboards/@services/ranges.js'
import { pickCurrency } from '../../../../../@utils/currencyDefault.js'
import { comparable } from './snapshot.js'

// The menu keys of the dashboards the goals open (`@services/menu.js` MENU_KEYS); a test checks they stay the same
export const DASHBOARD_KEYS = Object.freeze({
	SEASON_VS_LY: 'seasonVsLastYear',
	PRICE_CURVE: 'priceCurve',
	OFFICE_NETWORK: 'officeNetwork',
})

// In the order of measured use (bi-usage-and-dashboards.md § 2.3): P1 35 %, P3 22 %, P2 22 %, P4 15 %, the rest
export const GOALS = Object.freeze([
	{
		id: 'season', question: "How are my competitors' prices this season vs last year?",
		dashboard: { key: DASHBOARD_KEYS.SEASON_VS_LY, title: 'Season vs last year', params: 'cmp=ly&range=curNext&by=week', view: 'Compare with: Last year · Current and next month' },
		fills: 'Fills the form: provider × pick-up week',
	},
	{
		id: 'this-week', question: 'What changed this week for the coming weeks?',
		dashboard: { key: DASHBOARD_KEYS.SEASON_VS_LY, title: 'Season vs last year', params: 'cmp=lw&range=next4w&by=day', view: 'Compare with: Last week · Next 4 weeks' },
		fills: 'Fills the form: provider × pick-up date',
	},
	{
		id: 'date-curve', question: 'How did prices move for one pick-up date as it approached?',
		dashboard: { key: DASHBOARD_KEYS.PRICE_CURVE, title: 'Price curve of a date' },
		fills: 'Fills the form: provider × capture day',
	},
	{
		id: 'office-network', question: 'Where am I out of market across my offices?',
		dashboard: { key: DASHBOARD_KEYS.OFFICE_NETWORK, title: 'My office network' },
		fills: 'Fills the form: provider × office',
	},
	{ id: 'car-groups', question: 'Compare car groups over pick-up dates', fills: 'Fills the form: Acriss × pick-up date' },
	{ id: 'in-depth', question: 'Compare sites, rental lengths or offices in depth', fills: 'Fills the form — you choose what to compare' },
])

/** `{ kind: 'dashboard', key, path }` when the menu offers the goal's dashboard, else `{ kind: 'prefill' }` */
export function goalTarget(goal, menuEntries) {
	const entry = goal?.dashboard && (menuEntries || []).find(e => e?.key === goal.dashboard.key && e.path)
	if (!entry) return { kind: 'prefill' }
	return { kind: 'dashboard', key: entry.key, path: goal.dashboard.params ? `${entry.path}?${goal.dashboard.params}` : entry.path }
}

// The form the goals start from: what BIFormData says with nothing picked
const EMPTY = Object.freeze({
	siteGroupID: null, siteID: [], officePickupID: [], officeGroupID: null, officeDropoffID: null,
	startPickupDate: null, endPickupDate: null, startBookingDate: null, endBookingDate: null,
	lor: [], currency: null, isPreviousComparison: false, periodComp: {}, reportType: null,
	tiers: null, providerID: [], webSupplierID: [], categoryID: [], acriss: [],
	unlimited_km: null, insurance: null, is_prepaid: null, fuelPolicyID: null, automatic: null, in_terminal_pickup: null,
	isDailyPrice: false, groupBy: 'day', isNewVersion: true, urlId: null, officePickupIDAux: null,
})

const GROUP_LABEL = {
	'provider-pickup': 'Provider Pickup', 'provider-booking': 'Provider Booking', 'provider-office': 'Provider Office',
	'acriss-pickup': 'Acriss Pickup',
}
const BY_LABEL = { day: 'Day', week: 'Week', month: 'Month' }

// The "why" lines: their label, the label of their field in the band (`Filters/index.jsx`, to mark and light it) and the
// fields of the form each one stands for (to tell when the user changed it)
export const WHY_FIELDS = Object.freeze({
	group: { label: 'How to group', field: 'How to group', keys: ['reportType'] },
	offices: { label: 'Office', field: 'Pick up Office', keys: ['officePickupID'] },
	sites: { label: 'Site', field: 'Sites', keys: ['siteID'] },
	booking: { label: 'Prices captured', field: 'Booking date range', keys: ['startBookingDate', 'endBookingDate'] },
	pickup: { label: 'Pick-up', field: 'Pickup date range', keys: ['startPickupDate', 'endPickupDate'] },
	by: { label: 'Group by', field: 'Group by option', keys: ['groupBy'] },
	lor: { label: 'Length', field: 'Length of Rental', keys: ['lor'] },
	compare: { label: 'Compare', field: 'Days/Years compare', keys: ['isPreviousComparison', 'periodComp'] },
	currency: { label: 'Currency', field: 'Currency', keys: ['currency'] },
})
const WHY_ORDER = ['group', 'offices', 'sites', 'booking', 'pickup', 'by', 'lor', 'compare', 'currency']

const compact = range => [toCompact(range.from), toCompact(range.to)]

/**
 * What a goal fills: `{ form, why: [{ key, label, value, reason, choose? }], choose: [form keys] }`.
 * `ctx = { today, lastOffice, lastCurrency, offices: [{ value, label }], currencies: [{ value, label, selected }] }`.
 */
export function prefillGoal(goal, ctx = {}) {
	const today = startOfDay(ctx.today || new Date())
	const offices = Array.isArray(ctx.offices) ? ctx.offices : []
	const form = { ...EMPTY, siteID: [], officePickupID: [], lor: [], periodComp: {} }
	const why = {}
	const choose = []
	const set = (key, value, reason, isChoice = false) => { why[key] = { key, label: WHY_FIELDS[key].label, value, reason, ...(isChoice && { choose: true }) } }

	const capture = (key, label, range, reason) => {
		[form.startBookingDate, form.endBookingDate] = compact(range)
		set('booking', label, reason)
	}
	const pickup = (rangeKey, label, reason) => {
		[form.startPickupDate, form.endPickupDate] = compact(resolveRange(rangeKey, today))
		set('pickup', label, reason)
	}
	const group = reportType => { form.reportType = reportType; return GROUP_LABEL[reportType] }
	const byDay = reason => { form.groupBy = 'day'; set('by', BY_LABEL.day, reason) }

	// office: the one of the last query while the user still sees it (CA 5), else to choose
	const setOffice = () => {
		const last = ctx.lastOffice === null || ctx.lastOffice === undefined ? null : offices.find(o => String(o.value) === String(ctx.lastOffice))
		// the option's own value (an id of another type would be pruned by the ajax filter)
		if (last) { form.officePickupID = [last.value]; set('offices', last.label, 'the office of your last query') }
		else { choose.push('officePickupID'); set('offices', 'choose an office', offices.length ? 'you have no earlier query to start from' : 'no office to start from', true) }
	}
	const base = () => {
		form.lor = ['7']
		set('lor', '7 days', 'the most common: 9 in 10 questions use one length, almost always 7')
		const option = pickCurrency(ctx.currencies, ctx.lastCurrency)
		if (option) {
			form.currency = option.value
			const last = ctx.lastCurrency !== null && ctx.lastCurrency !== undefined && String(option.value) === String(ctx.lastCurrency)
			set('currency', option.label, last ? 'the currency of your last query' : "your company's default")
		} else {
			choose.push('currency')
			set('currency', 'choose one', 'no currency to start from', true)
		}
		set('compare', 'Single period', 'no comparison')
	}

	const last7 = { from: addDays(today, -7), to: today } // the band's "Last 7 Days" (@constants/date.js)
	const yesterday = { from: addDays(today, -1), to: addDays(today, -1) }

	switch (goal?.id) {
		case 'season':
			set('group', group('provider-pickup'), 'one line per provider, a column per pick-up week')
			setOffice(); base()
			pickup('curNext', 'Current and next month', 'the season ahead')
			capture('booking', 'Last 7 days', last7, 'the latest week of captured prices')
			form.groupBy = 'week'; set('by', BY_LABEL.week, 'one column per week of the season')
			form.isPreviousComparison = true; form.periodComp = { type: 'year', quantity: 1 }
			set('compare', '1 year', 'the same dates last year')
			break
		case 'this-week':
			set('group', group('provider-pickup'), 'one line per provider, a column per pick-up date')
			setOffice(); base()
			pickup('next4w', 'Next 4 weeks', 'the coming pick-ups')
			capture('booking', 'Yesterday', yesterday, 'the latest full day of captured prices')
			byDay('one column per pick-up date')
			form.isPreviousComparison = true; form.periodComp = { type: 'day', quantity: 7 }
			set('compare', '7 days', 'what the same pick-ups cost a week earlier')
			break
		case 'date-curve':
			set('group', group('provider-booking'), 'one line per provider, across the days the price was captured')
			setOffice(); base()
			choose.push('startPickupDate')
			set('pickup', 'choose one date', 'this question follows a single pick-up day', true)
			capture('booking', 'Last 8 weeks', { from: addDays(today, -56), to: today }, 'prices captured before that day; most of these questions look 1-3 months back')
			byDay('one point per capture day')
			set('compare', 'Single period', 'most of these questions do not compare')
			break
		case 'office-network':
			set('group', group('provider-office'), 'one column per office')
			if (offices.length) { form.officePickupID = offices.map(o => o.value); set('offices', `All ${offices.length} offices`, 'every office you see in BI') }
			else { choose.push('officePickupID'); set('offices', 'choose an office', 'no office to start from', true) }
			base()
			capture('booking', 'Yesterday', yesterday, 'the latest full day of captured prices')
			pickup('next4w', 'Next 4 weeks', 'the coming pick-ups')
			choose.push('siteID')
			set('sites', 'choose one site', 'offices compare best on one website (8 in 10 of these questions use one)', true)
			form.groupBy = 'day'
			break
		case 'car-groups':
			set('group', group('acriss-pickup'), 'one line per Acriss code; switch to Category Pickup for categories')
			setOffice(); base()
			pickup('next4w', 'Next 4 weeks', 'the most common look ahead')
			capture('booking', 'Last 7 days', last7, 'the latest week of captured prices')
			byDay('7 in 10 questions')
			break
		case 'in-depth':
			choose.push('reportType')
			set('group', 'choose what to compare', 'Site Booking, Lor Pickup or Office Booking', true)
			setOffice(); base()
			pickup('next4w', 'Next 4 weeks', 'the most common look ahead')
			capture('booking', 'Last 7 days', last7, 'the latest week of captured prices')
			form.groupBy = 'day'
			break
		default:
			break
	}
	return { form, why: WHY_ORDER.filter(k => why[k]).map(k => why[k]), choose }
}

const isEmpty = v => v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length)

/** The goal's choices still empty in `form`: they block Search ("Missing: …") */
export const goalMissing = (choose, form) => (choose || []).filter(k => isEmpty(form?.[k]))

const pick = (form, keys) => Object.fromEntries(keys.map(k => [k, form?.[k]]))

/**
 * The "why" lines against the form now: `changed` when the user changed a filled value, `chosen` when a choice was
 * made. By value — the ajax filters re-fire `onChange` on their own when they reload.
 */
export function whyState(lines, filledForm, form) {
	return (lines || []).map(line => {
		const keys = WHY_FIELDS[line.key]?.keys || []
		const differs = comparable(pick(filledForm, keys)) !== comparable(pick(form, keys))
		return line.choose ? { ...line, chosen: keys.every(k => !isEmpty(form?.[k])) } : { ...line, changed: differs }
	})
}

// T4: per user and browser, like Season's view (`viewState.js` storageKey)
export const goalsStorageKey = uid => `rentacar.bi.goals.${uid || 'anon'}`

/** The questions open (the default) unless this user closed them with "Go straight to the form" */
export function readGoalsOpen(storage, uid) {
	try {
		return storage?.getItem?.(goalsStorageKey(uid)) !== 'closed'
	} catch {
		return true
	}
}

export function writeGoalsOpen(storage, uid, open) {
	try {
		storage?.setItem?.(goalsStorageKey(uid), open ? 'open' : 'closed')
	} catch {}
}

/** The `/results` body: the form, plus the goal it came from (CA 17) — never inside the form, so never in a link */
export const submitBody = (form, goalId) => (goalId ? { ...form, biGoal: goalId } : form)

/** "Clear form": the form as the page opens it — pick-ups today + 7 (`Filters/index.jsx`), captures the last 7 days
 * up to yesterday (`@signals/filters/date.js`), the company currency, nothing else */
export function clearedForm(ctx = {}) {
	const today = startOfDay(ctx.today || new Date())
	const [startPickupDate, endPickupDate] = compact({ from: today, to: addDays(today, 7) })
	const [startBookingDate, endBookingDate] = compact({ from: addDays(today, -7), to: addDays(today, -1) })
	return { ...EMPTY, siteID: [], officePickupID: [], lor: [], periodComp: {}, startPickupDate, endPickupDate, startBookingDate, endBookingDate,
		currency: pickCurrency(ctx.currencies, null)?.value ?? null }
}

/**
 * How each field of the band is marked while a question's prefill is active (UX Obs. 4-5, mock `.field.auto` /
 * `.field.choose`): `{ <field label>: 'filled' | 'choose' }` — a blue dot on what the question filled and the user
 * kept, an amber dashed border on what is still to choose. `lines` = `whyState(...)`.
 */
export function fieldMarks(lines) {
	const marks = {}
	for (const line of lines || []) {
		const field = WHY_FIELDS[line.key]?.field
		if (!field) continue
		if (line.choose) { if (!line.chosen) marks[field] = 'choose' }
		else if (!line.changed) marks[field] = 'filled'
	}
	return marks
}
